import PropTypes from 'prop-types';
import {
  Box, Typography, TextField, IconButton, CircularProgress, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Forum, Chat, MarkChatUnread, Alarm, NotificationsActive,
  Public, Dashboard, Visibility, Remove, Add, Check,
  EventAvailable, CheckCircle, Cancel,
} from '@mui/icons-material';
import { brandColors } from '../../constants/brandColors';
import { softTints, slate, cardShadow } from '../../constants/uiTokens';
import WaCard from './WaCard';
import WaSwitch from './WaSwitch';
import WhatsPreview from './WhatsPreview';

const TITLE_ICON = { fontSize: 22, color: 'primary.dark' };
const FALLBACK_MODELS = [{ id: 1, name: 'Modelo 1' }, { id: 2, name: 'Modelo 2' }];
const CARD_SX = {
  bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
  borderRadius: '10px', boxShadow: cardShadow, p: { xs: 2, sm: 2.75 },
};

function clampInt(v, min, max) {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

// Hora inteira (0–24) ⇄ string "HH:00" para o input type=time.
function hourToTime(h) {
  return `${String(clampInt(h, 0, 24)).padStart(2, '0')}:00`;
}
function timeToHour(v) {
  return parseInt(String(v).split(':')[0], 10) || 0;
}

/** Linha de checkbox custom (estilo do mockup) para os eventos do dono. */
function CheckRow({ checked, icon, iconColor, label, onToggle }) {
  return (
    <Box onClick={onToggle} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
      <Box
        sx={{
          width: 20, height: 20, borderRadius: '5px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: checked ? 'primary.main' : '#fff',
          border: checked ? '1px solid' : '1.5px solid',
          borderColor: checked ? 'primary.main' : '#c2cad1',
          transition: 'all .12s',
        }}
      >
        {checked && <Check sx={{ fontSize: 16, color: '#fff' }} />}
      </Box>
      {icon && <Box component="span" sx={{ color: iconColor, display: 'flex' }}>{icon}</Box>}
      <Typography sx={{ fontSize: 13.5, color: slate[700] }}>{label}</Typography>
    </Box>
  );
}
CheckRow.propTypes = {
  checked: PropTypes.bool,
  icon: PropTypes.node,
  iconColor: PropTypes.string,
  label: PropTypes.string,
  onToggle: PropTypes.func,
};

/** Linha de origem (Site público / Painel) na seção de avisos. */
function OriginRow({ icon, title, desc, checked, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: 1.75, border: '1px solid', borderColor: '#eaeef1', borderRadius: '9px' }}>
      <Box component="span" sx={{ color: slate[600], display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{desc}</Typography>
      </Box>
      <WaSwitch checked={checked} onChange={onChange} ariaLabel={title} />
    </Box>
  );
}
OriginRow.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string,
  desc: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
};

/**
 * WhatsAppConfig — estado ATIVADO: as 5 seções de configuração (status, mensagem,
 * avisos, lembrete, dono) + a coluna de pré-visualização ao vivo (sticky no desktop,
 * inline logo após a mensagem no mobile). Apresentacional: recebe form + handlers.
 */
export default function WhatsAppConfig({
  form, updateField, models,
  onDisable, togglingEnabled,
  previewText, previewShowButtons, previewEmpty,
}) {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const modelList = models?.length ? models : FALLBACK_MODELS;

  // ── Status ──────────────────────────────────────────────
  const statusCard = (
    <Box sx={{ ...CARD_SX, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ width: 46, height: 46, borderRadius: '12px', bgcolor: softTints.success.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Forum sx={{ fontSize: 26, color: brandColors.whatsapp.action }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>WhatsApp</Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 11.5, fontWeight: 600, color: softTints.success.fg, bgcolor: softTints.success.bg, px: 1, py: '3px', borderRadius: 999 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: brandColors.whatsapp.action }} />
            Ativo
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: slate[500], mt: '2px' }}>Pronto para enviar e receber mensagens.</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>Ativado</Typography>
        {togglingEnabled
          ? <CircularProgress size={20} />
          : <WaSwitch checked onChange={(next) => { if (!next) onDisable(); }} ariaLabel="Desativar WhatsApp" />}
      </Box>
    </Box>
  );

  // ── Mensagem ────────────────────────────────────────────
  const messageCard = (
    <WaCard
      icon={<Chat sx={TITLE_ICON} />}
      title="Mensagem ao cliente"
      description="Escolha um modelo e ajuste o texto. A pré-visualização à direita atualiza ao vivo."
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: slate[700], mb: 1 }}>Modelo de mensagem</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.25 }}>
        {modelList.map((m) => {
          const active = form.templateStyle === m.id;
          return (
            <Box
              key={m.id}
              role="button"
              tabIndex={0}
              onClick={() => updateField('templateStyle', m.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); updateField('templateStyle', m.id); } }}
              sx={{
                cursor: 'pointer', fontSize: 13, fontWeight: 600, px: 1.75, py: 1, borderRadius: '8px',
                border: '1px solid',
                borderColor: active ? 'primary.main' : '#d6dde3',
                bgcolor: active ? softTints.info.bg : '#fff',
                color: active ? 'primary.dark' : slate[600],
                transition: 'all .12s',
              }}
            >
              {m.name}
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: 13, fontWeight: 600, color: slate[700], mb: 1 }}>Orientações ao cliente (opcional)</Typography>
      <TextField
        value={form.messageNotes}
        onChange={(e) => updateField('messageNotes', e.target.value)}
        placeholder="Ex.: vir em jejum, trazer documento, chegar 10 min antes…"
        multiline
        minRows={3}
        maxRows={8}
        fullWidth
        inputProps={{ maxLength: 1000 }}
      />
      <Typography sx={{ fontSize: 11.5, color: slate[500], mt: 1 }}>
        O texto entra na mensagem ao cliente. Variáveis como nome e horário são preenchidas automaticamente pelo modelo.
      </Typography>
    </WaCard>
  );

  // ── Avisos ──────────────────────────────────────────────
  const avisosCard = (
    <WaCard
      icon={<MarkChatUnread sx={TITLE_ICON} />}
      title="Avisar ao criar agendamento"
      description="Enviar a mensagem assim que um agendamento for criado — controle por origem."
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <OriginRow
          icon={<Public sx={{ fontSize: 24 }} />}
          title="Site público"
          desc="Cliente agendou pela sua página de agendamento online."
          checked={form.notifyOnPublicCreate}
          onChange={(next) => updateField('notifyOnPublicCreate', next)}
        />
        <OriginRow
          icon={<Dashboard sx={{ fontSize: 24 }} />}
          title="Painel"
          desc="Você ou a equipe criou o agendamento internamente."
          checked={form.notifyOnPrivateCreate}
          onChange={(next) => updateField('notifyOnPrivateCreate', next)}
        />
      </Box>
    </WaCard>
  );

  // ── Lembrete ────────────────────────────────────────────
  const lembreteCard = (
    <WaCard
      icon={<Alarm sx={TITLE_ICON} />}
      title="Lembrete antes do horário"
      description="Mensagem automática com botões Confirmar / Cancelar antes da consulta."
      action={<WaSwitch checked={form.reminderEnabled} onChange={(next) => updateField('reminderEnabled', next)} ariaLabel="Ativar lembrete" />}
      dim={!form.reminderEnabled}
    >
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', ml: { sm: '34px' } }}>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: slate[700], mb: 1 }}>Antecedência</Typography>
          <Box sx={{ display: 'flex', alignItems: 'stretch', border: '1px solid', borderColor: '#cdd5dc', borderRadius: '8px', overflow: 'hidden', width: 'fit-content' }}>
            <IconButton
              onClick={() => updateField('reminderHoursBefore', clampInt(form.reminderHoursBefore, 1, 168) - 1)}
              sx={{ width: 40, borderRadius: 0, bgcolor: '#f3f5f7', color: slate[700] }}
            ><Remove fontSize="small" /></IconButton>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, px: 2, justifyContent: 'center', minWidth: 96, bgcolor: '#fff' }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'text.primary', alignSelf: 'center' }}>{form.reminderHoursBefore}</Typography>
              <Typography sx={{ fontSize: 12, color: slate[500], alignSelf: 'center' }}>horas antes</Typography>
            </Box>
            <IconButton
              onClick={() => updateField('reminderHoursBefore', clampInt(form.reminderHoursBefore, 1, 168) + 1)}
              sx={{ width: 40, borderRadius: 0, bgcolor: '#f3f5f7', color: slate[700] }}
            ><Add fontSize="small" /></IconButton>
          </Box>
          <Typography sx={{ fontSize: 11.5, color: slate[500], mt: 0.75 }}>Entre 1 e 168 horas (até 7 dias).</Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: slate[700] }}>Respeitar horário comercial</Typography>
            <WaSwitch checked={form.reminderRespectBusinessHours} onChange={(next) => updateField('reminderRespectBusinessHours', next)} ariaLabel="Respeitar horário comercial" />
          </Box>
          {form.reminderRespectBusinessHours && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: slate[500] }}>das</Typography>
                <TextField
                  type="time" size="small" value={hourToTime(form.reminderBusinessStart)}
                  onChange={(e) => updateField('reminderBusinessStart', timeToHour(e.target.value))}
                  inputProps={{ step: 3600 }} sx={{ width: 130 }}
                />
                <Typography sx={{ fontSize: 13, color: slate[500] }}>às</Typography>
                <TextField
                  type="time" size="small" value={hourToTime(form.reminderBusinessEnd)}
                  onChange={(e) => updateField('reminderBusinessEnd', timeToHour(e.target.value))}
                  inputProps={{ step: 3600 }} sx={{ width: 130 }}
                />
              </Box>
              <Typography sx={{ fontSize: 11.5, color: slate[500], mt: 0.75 }}>
                Lembretes fora da janela são adiados para o próximo horário válido.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </WaCard>
  );

  // ── Dono ────────────────────────────────────────────────
  const ownerCard = (
    <WaCard
      icon={<NotificationsActive sx={TITLE_ICON} />}
      title="Notificações para você (dono)"
      description="Receba avisos no seu WhatsApp quando algo acontecer na agenda. Cada aviso consome 1 crédito."
      action={<WaSwitch checked={form.ownerNotifyEnabled} onChange={(next) => updateField('ownerNotifyEnabled', next)} ariaLabel="Ativar notificações do dono" />}
      dim={!form.ownerNotifyEnabled}
    >
      <Box sx={{ ml: { sm: '34px' } }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: slate[700], mb: 1 }}>Seu WhatsApp (com DDD)</Typography>
        <TextField
          value={form.ownerPhone}
          onChange={(e) => updateField('ownerPhone', e.target.value)}
          placeholder="(31) 99999-0000"
          inputProps={{ inputMode: 'tel', maxLength: 20 }}
          size="small"
          sx={{ width: 240, maxWidth: '100%', mb: 2.25 }}
        />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: slate[700], mb: 1.25 }}>Avisar quando…</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <CheckRow
            checked={form.ownerNotifyOnCreate}
            icon={<EventAvailable sx={{ fontSize: 20 }} />} iconColor="primary.main"
            label="Novo agendamento criado"
            onToggle={() => updateField('ownerNotifyOnCreate', !form.ownerNotifyOnCreate)}
          />
          <CheckRow
            checked={form.ownerNotifyOnConfirm}
            icon={<CheckCircle sx={{ fontSize: 20 }} />} iconColor={brandColors.whatsapp.action}
            label="Cliente confirmou presença"
            onToggle={() => updateField('ownerNotifyOnConfirm', !form.ownerNotifyOnConfirm)}
          />
          <CheckRow
            checked={form.ownerNotifyOnCancel}
            icon={<Cancel sx={{ fontSize: 20 }} />} iconColor="error.main"
            label="Cliente cancelou"
            onToggle={() => updateField('ownerNotifyOnCancel', !form.ownerNotifyOnCancel)}
          />
        </Box>
      </Box>
    </WaCard>
  );

  // ── Preview ─────────────────────────────────────────────
  const previewPanel = (
    <Box sx={isLg ? { position: 'sticky', top: 20 } : undefined}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Visibility sx={{ fontSize: 20, color: brandColors.whatsapp.action }} />
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>Pré-visualização ao vivo</Typography>
      </Box>
      <WhatsPreview text={previewText} showButtons={previewShowButtons} empty={previewEmpty} />
      <Typography sx={{ mt: 1.5, fontSize: 11.5, color: slate[500], textAlign: 'center' }}>
        Cada mensagem enviada consome 1 crédito.
      </Typography>
    </Box>
  );

  if (isLg) {
    return (
      // alignItems padrão (stretch) é proposital: faz a coluna do preview ter a
      // altura da esquerda, dando "trilho" para o sticky acompanhar a rolagem (fix 4).
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {statusCard}{messageCard}{avisosCard}{lembreteCard}{ownerCard}
        </Box>
        <Box sx={{ width: 360, flexShrink: 0 }}>{previewPanel}</Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {statusCard}{messageCard}{previewPanel}{avisosCard}{lembreteCard}{ownerCard}
    </Box>
  );
}

WhatsAppConfig.propTypes = {
  form: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  models: PropTypes.array,
  onDisable: PropTypes.func.isRequired,
  togglingEnabled: PropTypes.bool,
  previewText: PropTypes.string,
  previewShowButtons: PropTypes.bool,
  previewEmpty: PropTypes.bool,
};
